'use client'

import { signup } from 'app/actions/auth'
import useDictionary from 'app/dictionaries/useDictionary'
import GenericForm from 'components/Form'
import LoadingAnimation from 'components/layout/LoadingAnimation'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { boolean, object, ref, string } from 'yup'

type SignupFormData = {
    first_name: string
    last_name: string
    username: string
    email: string
    password: string
    password_verify: string
    country: string
    eafc_id?: string
    psn_id?: string
    xbox_id?: string
    discord_id?: string
    turnstile?: string
}

interface SignupFormProps {
    countries: ICountry[]
    domainName: string
    lang: string
}

const SignupForm = ({ countries, domainName, lang }: SignupFormProps) => {
    const [step, setStep] = useState<number>(1)
    const dict = useDictionary()

    if (!dict) {
        return <LoadingAnimation className='!size-7' />
    }

    const d = dict as Record<string, string>

    const fields: TFormField[] = [
        {
            name: 'first_name',
            label: d['firstName'] ?? 'First Name',
            type: 'text',
            required: true,
            placeholder: d['enterFirstNameHere'] ?? 'Enter first name'
        },
        {
            name: 'last_name',
            label: d['lastName'] ?? 'Last Name',
            type: 'text',
            required: true,
            placeholder: d['enterLastNameHere'] ?? 'Enter last name'
        },
        {
            name: 'username',
            label: d['username'] ?? 'Username',
            type: 'text',
            required: true,
            placeholder: d['enterUsernameHere'] ?? 'Enter username'
        },
        {
            name: 'email',
            label: d['emailAddress'] ?? 'Email',
            type: 'email',
            required: true,
            placeholder: d['enterEmailHere'] ?? 'Enter email'
        },
        {
            name: 'password',
            label: d['password'] ?? 'Password',
            type: 'password',
            required: true,
            placeholder: d['pleaseEnterYourPassword'] ?? 'Enter password'
        },
        {
            name: 'password_verify',
            label: d['passwordConfirmation'] ?? 'Confirm password',
            type: 'password',
            required: true,
            placeholder: d['confirmPassword'] ?? 'Confirm password'
        },
        {
            name: 'country',
            label: d['country'] ?? 'Country',
            type: 'select',
            required: true,
            options: countries.map(country => ({ id: country.name, name: country.name }))
        },
        {
            name: 'terms',
            label: (
                <>
                    {d['iAgreeToYour'] ?? 'I agree to'}{' '}
                    <Link href={`/${lang}/info/tos`} target='_blank' className='text-primary underline !lowercase'>
                        {d['termsOfServices'] ?? 'Terms of services'}
                    </Link>
                </>
            ),
            type: 'checkbox',
            required: true
        },
        {
            name: 'turnstile',
            type: 'turnstile',
            required: true,
            label: ''
        }
    ]

    const validationSchema = object({
        first_name: string()
            .min(2, 'First name must be at least 2 characters')
            .trim()
            .max(100, 'First name cannot exceed 100 characters')
            .required('First name is required'),
        last_name: string()
            .min(2, 'Last name must be at least 2 characters')
            .trim()
            .max(100, 'Last name cannot exceed 100 characters')
            .required('Last name is required'),
        username: string()
            .min(4, 'Username must be at least 4 characters')
            .max(20, 'Username cannot exceed 20 characters')
            .matches(/^[a-zA-Z0-9]+$/, 'Username must contain only letters and numbers')
            .trim()
            .required('Username is required'),
        email: string()
            .required('Email is required')
            .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email format'),
        password: string()
            .min(8, 'Password must be at least 8 characters')
            .max(128, 'Password cannot exceed 128 characters')
            .matches(/[a-z]/, 'Password requires lowercase')
            .matches(/[A-Z]/, 'Password requires uppercase')
            .matches(/[0-9]/, 'Password requires number')
            .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password requires special character')
            .required('Password is required'),
        password_verify: string()
            .oneOf([ref('password')], 'Passwords do not match')
            .required('Password confirmation is required'),
        country: string().required('Country is required'),
        terms: boolean().oneOf([true], 'You must agree to the terms').required('You must agree to the terms'),
        turnstile: string().required('ReCAPTCHA is required'),
        eafc_id: string().notRequired(),
        psn_id: string().notRequired(),
        xbox_id: string().notRequired(),
        discord_id: string().notRequired()
    })

    const onSubmit = async (formData: FormData): Promise<ApiResponse> => {
        const data: SignupFormData = {
            first_name: formData.get('first_name')?.toString() || '',
            last_name: formData.get('last_name')?.toString() || '',
            username: formData.get('username')?.toString() || '',
            email: formData.get('email')?.toString() || '',
            password: formData.get('password')?.toString() || '',
            password_verify: formData.get('password_verify')?.toString() || '',
            country: formData.get('country')?.toString() || '',
            eafc_id: '',
            psn_id: '',
            xbox_id: '',
            discord_id: ''
        }

        const submitData = { ...data }

        const response = await signup(submitData, domainName)
        console.log('signup response:', response)
        if (response.success) setStep(2)
        return response
    }

    if (step === 1) {
        return (
            <GenericForm
                {...{
                    fields,
                    onSubmit,
                    buttonTexts: { save: d['register'] ?? 'Register', saving: 'Registering...' },
                    validationSchema,
                    keepLoadingOnSuccess: true,
                    getErrorDisplayMessage: result => {
                        // Use translated message if available, otherwise use backend message directly
                        const code = result.error_code ?? result.error
                        if (code && d[code]) {
                            return d[code]
                        }
                        // Backend messages are already safe and user-friendly
                        return result.message ?? null
                    }
                }}
            />
        )
    }

    return (
        <div className='flex flex-col gap-2 justify-center items-center w-full h-full'>
            <div className='flex justify-center'>
                <Image src='/images/icons/mail.png' alt='mail_img' width={120} height={120} />
            </div>
            <div className='w-full text-2xl font-bold leading-6 p-2 text-center text-zinc-200'>
                {d['checkYourEmail'] ?? 'Check your email'}
            </div>
            <p className='w-full text-base font-light p-2 leading-7 text-center text-zinc-200'>
                {d['weSentYouAVerificationLinkPleaseGoClickIt'] ??
                    'We sent you a verification link, please go click it'}
            </p>
        </div>
    )
}

export default SignupForm
